/**
 * Side nav bookmarks (SAL-3283): the rules both ends share. The server
 * actions (`server-actions/nav-bookmarks.js`) validate with them and the nav
 * (`components/nav/useNavBookmarks.js`) renders with them.
 *
 * A bookmark is a page address inside the app, saved per member and per
 * studio, with the name and icon it had when it was saved. A page that is
 * still in a menu is shown under that menu's current label and icon, so a
 * studio renaming a module renames its bookmarks too. A page the manifest
 * lists but this viewer's menus no longer show (a role change, a plugin
 * switched off) is hidden rather than deleted, so it comes back if the page
 * does.
 *
 * Plain data and functions only, like the manifest, so it can be imported on
 * either side.
 */

import { NAV_MANIFEST, TRAINING_MODE_NAV } from "@/lib/navigation/nav-manifest";

export const MAX_NAV_BOOKMARKS = 8;
export const NAV_BOOKMARKS_FULL_MESSAGE = `You can keep up to ${MAX_NAV_BOOKMARKS} bookmarks. Remove one to add another.`;

const MAX_HREF_LENGTH = 512;
const MAX_LABEL_LENGTH = 120;
const ICON_NAME = /^[A-Za-z0-9]{1,64}$/;
const ORIGIN = "https://app.invalid";

/**
 * The in-app address to store, or null for anything that is not one: another
 * site, a protocol-relative `//host`, an API route, or something too long.
 * Returned as path + query + hash, the way the browser resolved it.
 */
export function normalizeBookmarkHref(href) {
  if (typeof href !== "string") return null;
  const raw = href.trim();
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) {
    return null;
  }
  let url;
  try {
    url = new URL(raw, ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== ORIGIN || url.pathname.startsWith("/api/")) return null;
  const normalized = `${url.pathname}${url.search}${url.hash}`;
  return normalized.length <= MAX_HREF_LENGTH ? normalized : null;
}

/** A bookmark's name on one line, or null when there is nothing to show. */
export function normalizeBookmarkLabel(label) {
  if (typeof label !== "string") return null;
  const text = label.replace(/\s+/g, " ").trim();
  return text ? text.slice(0, MAX_LABEL_LENGTH) : null;
}

/** `{ href, label, iconName }` ready to store, or null when it cannot be. */
export function parseNavBookmark({ href, label, iconName } = {}) {
  const cleanHref = normalizeBookmarkHref(href);
  const cleanLabel = normalizeBookmarkLabel(label);
  if (!cleanHref || !cleanLabel) return null;
  return {
    href: cleanHref,
    label: cleanLabel,
    iconName:
      typeof iconName === "string" && ICON_NAME.test(iconName)
        ? iconName
        : null,
  };
}

function collectHrefs(items, into, onlyVisible) {
  for (const item of items || []) {
    if (!item || (onlyVisible && item.display === false)) continue;
    if (typeof item.href === "string" && !item.external) {
      into.set(item.href, item);
    }
    collectHrefs(item.subItems, into, onlyVisible);
  }
  return into;
}

// Every route the manifest declares. A per-studio href is still a template
// here ("{workspaceId}"), so it never equals a saved address and such a
// bookmark is simply shown; the page's own guard still decides who opens it.
const MANIFEST_HREFS = new Set(
  collectHrefs(
    [...NAV_MANIFEST, TRAINING_MODE_NAV].flatMap((menu) => menu.items),
    new Map(),
    false,
  ).keys(),
);

/** href → link, for every link this viewer's menus show. */
export function collectVisibleNavLinks(menus) {
  return collectHrefs(
    (menus || []).flatMap((menu) => menu.items),
    new Map(),
    true,
  );
}

const routeOf = (href) => href.split(/[?#]/)[0];

// Every route the manifest declares, without query or hash, so a bookmark of
// a menu page saved with a tab or an anchor still belongs to that page.
const MANIFEST_ROUTES = new Set([...MANIFEST_HREFS].map(routeOf));

/**
 * The saved bookmarks as panel links for this viewer: renamed to the menu's
 * current label where the page is still in a menu, and without the ones whose
 * page the manifest lists but this viewer's menus do not show. A saved
 * address with a query or hash is matched to its page by route, and keeps the
 * name it was saved under: it is a view of that page, not the menu link.
 */
export function resolveNavBookmarks(bookmarks, visibleLinks) {
  const visibleRoutes = new Set([...visibleLinks.keys()].map(routeOf));
  return (bookmarks || []).flatMap((bookmark) => {
    const link = visibleLinks.get(bookmark.href);
    if (link) {
      return [
        {
          href: bookmark.href,
          label: link.label,
          iconName: link.iconName || bookmark.iconName,
        },
      ];
    }
    const route = routeOf(bookmark.href);
    if (
      MANIFEST_HREFS.has(bookmark.href) ||
      (MANIFEST_ROUTES.has(route) && !visibleRoutes.has(route))
    ) {
      return [];
    }
    return [
      { href: bookmark.href, label: bookmark.label, iconName: bookmark.iconName },
    ];
  });
}
